import { hex, type Color } from "../../common/color.js";
import type { Rect } from "../../common/geometry.js";
import type { DrawableTexture, TextSpan } from "../render/RenderContext.js";
import type { Theme } from "../themes/Theme.js";

/**
 * Where an item's picture comes from.
 *
 * `source` is a sub-rectangle in texture pixels, so a whole inventory's worth
 * of icons can live on one sheet and cost one texture bind between them. The
 * renderer has taken a source rect since it could draw an image at all;
 * nothing new was needed here.
 */
export interface ItemSprite {
  texture: DrawableTexture;
  source?: Rect;
}

/**
 * One stack of one thing.
 *
 * Deliberately thin. Webwin has no opinion about what an item *is* — damage,
 * durability, sockets, bind-on-pickup are the game's business — so `data`
 * carries whatever the game needs and the fields here are only the ones a
 * slot has to draw or a grid has to place.
 */
export interface ItemStack {
  /** Identifies the *kind* of thing, so two stacks know if they can merge. */
  id: string;
  name: string;
  sprite?: ItemSprite | null;
  /** Stack size. Undefined and 1 both mean "one of these, do not label it". */
  count?: number;
  /** Largest this stack can grow. 1, or undefined, means it does not stack. */
  maxStack?: number;
  /** Key into a rarity table; unknown keys fall back to `common`. */
  rarity?: string;
  /** Cells wide and tall in a grid. Both default to 1. */
  width?: number;
  height?: number;
  /** Lines shown under the name in a tooltip. */
  description?: readonly string[];
  /**
   * Untouched by a hotbar's global cooldown: it fires through one, and the wash
   * is not drawn over its slot. Its own cooldown still holds it.
   */
  offGlobal?: boolean;
  /** Whatever the game wants to hang off it. */
  data?: unknown;
}

export interface Rarity {
  name: string;
  color: Color;
}

/**
 * The five-tier ladder most of the genre uses, in the colors it uses.
 *
 * Hard-coded rather than drawn from the palette, and on purpose: rarity
 * color is a language players already read, and a theme swapping purple for
 * its own accent would be changing what the item *is*. A game with a
 * different ladder passes its own table to the controls that take one.
 */
export const DEFAULT_RARITIES: Readonly<Record<string, Rarity>> = {
  common: { name: "Common", color: hex("#9d9d9d") },
  uncommon: { name: "Uncommon", color: hex("#1eff00") },
  rare: { name: "Rare", color: hex("#0070dd") },
  epic: { name: "Epic", color: hex("#a335ee") },
  legendary: { name: "Legendary", color: hex("#ff8000") },
};

export type RarityTable = Readonly<Record<string, Rarity>>;

/** The rarity for a stack, falling back to common. */
export function rarityOf(item: ItemStack | null, table: RarityTable = DEFAULT_RARITIES): Rarity {
  return (item?.rarity ? table[item.rarity] : undefined) ?? table.common ?? DEFAULT_RARITIES.common!;
}

/** Cells the stack occupies. Both are at least 1. */
export function itemSize(item: ItemStack): { width: number; height: number } {
  return {
    width: Math.max(1, Math.floor(item.width ?? 1)),
    height: Math.max(1, Math.floor(item.height ?? 1)),
  };
}

/** How many more of this kind the stack can take. */
export function roomIn(item: ItemStack | null): number {
  if (!item) return 0;
  const max = item.maxStack ?? 1;
  return Math.max(0, max - (item.count ?? 1));
}

/** True when `b` can be poured into `a`. */
export function canStack(a: ItemStack | null, b: ItemStack | null): boolean {
  if (!a || !b || a.id !== b.id) return false;
  return roomIn(a) > 0;
}

/**
 * Pour `from` into `into` as far as it will go.
 *
 * Returns new stacks rather than editing them: an inventory that mutates the
 * item it was handed cannot undo a move that the drop then refused. `from` is
 * null when it was emptied entirely.
 */
export function mergeStacks(
  into: ItemStack,
  from: ItemStack,
): { into: ItemStack; from: ItemStack | null } {
  const room = roomIn(into);
  if (room <= 0 || into.id !== from.id) return { into, from };
  const moved = Math.min(room, from.count ?? 1);
  const left = (from.count ?? 1) - moved;
  return {
    into: { ...into, count: (into.count ?? 1) + moved },
    from: left > 0 ? { ...from, count: left } : null,
  };
}

/**
 * Split `count` off a stack. Returns the piece taken and what is left, or
 * nulls when the split makes no sense — which is what a right-click drag on a
 * single item should do: nothing.
 */
export function splitStack(
  stack: ItemStack,
  count: number,
): { taken: ItemStack | null; left: ItemStack | null } {
  const have = stack.count ?? 1;
  const take = Math.floor(Math.min(Math.max(count, 0), have));
  if (take <= 0) return { taken: null, left: stack };
  if (take >= have) return { taken: stack, left: null };
  return { taken: { ...stack, count: take }, left: { ...stack, count: have - take } };
}

/**
 * The standard item tooltip: the name in its rarity color, the rarity under
 * it, then the description lines.
 *
 * Handed to `control.tooltip`, which takes spans as readily as a string.
 */
export function itemTooltip(
  item: ItemStack,
  theme: Theme,
  table: RarityTable = DEFAULT_RARITIES,
): TextSpan[] {
  const rarity = rarityOf(item, table);
  const spans: TextSpan[] = [
    { text: item.name, color: rarity.color, font: theme.metrics.captionFont },
  ];
  if (item.rarity) spans.push({ text: `\n${rarity.name}`, color: rarity.color });
  for (const line of item.description ?? []) {
    spans.push({ text: `\n${line}`, color: theme.palette.textSubtle });
  }
  const max = item.maxStack ?? 1;
  if (max > 1) {
    spans.push({ text: `\n${item.count ?? 1} / ${max}`, color: theme.palette.textSubtle });
  }
  return spans;
}

/** What an item drag carries. `DragPayload.kind` is this. */
export const ITEM_DRAG_KIND = "webwin.item";

/**
 * The payload's `data` for an item drag.
 *
 * The last two fields are how a move between two *different* containers is
 * settled. Within one container the receiver writes both ends itself and
 * neither is set; across two, the receiver can only write its own end, so it
 * says so with `takenFromSource` and leaves whatever the source should keep
 * — a swapped-out item, or the part of a stack that did not fit — in
 * `remainder`. The source reads both on `dragend`. Without this the item is
 * in two places at once, which is the classic duplication bug.
 */
export interface ItemDragData {
  item: ItemStack;
  /** Where it came from, so a refused drop can be put back. */
  fromIndex: number;
  /** Set by a receiver in another container that took the item. */
  takenFromSource?: boolean;
  /** What the source should hold instead. Null empties it. */
  remainder?: ItemStack | null;
}
